import React from 'react';
import './App.css';
import Oracle from './Oracle';
import defaultState from '../defaultState';
import program from '../program';

const localStorageName = 'productSelectorData';
const { useLocalStorage } = program;

class App extends React.Component {
  constructor({ onPageChange }) {
    super();
    this.program = program;
    this.onPageChange = onPageChange;
    this.state = this.getInitialState();
  }

  dispatch(action) {
    const { type, value } = action;
    const after = () => {
      this.saveState();
    };
    const nextPage = this.getNextPage();
    const currentPage = this.getCurrentPage();

    switch (type) {

      /* Navigation */
      case 'navigate':
        if (value > 0 && nextPage) { // Next
          this.setState((prevState) => ({
            ...prevState,
            history: [
              {
                id: nextPage.id,
                skipped: false,
              },
              ...prevState.history,
            ]
          }), after);
        } else if (value < 0) { // Back
          this.setState((prevState) => ({
            ...prevState,
            history: [
              ...prevState.history.slice(1),
              ...(
                prevState.history.length < 2
                  ? defaultState.history
                  : []
              ),
            ]
          }), after);
        } else {
          console.warn(
            'Attempted navigation could not be performed:',
            {
              action,
              nextPage
            }
          );
        }
      break;

      case 'skip':
        this.setState((prevState) => ({
          ...prevState,

          // Wipe out any values that the user may have selected
          // for this page's prompts.
          selections: {
            ...prevState.selections,
            ...currentPage.prompts.reduce(
              (acc, { id }) => ({
                ...acc,
                [id]: []
              }), {}
            ),
          },

          // Mark this page as skipped.
          history: [
            {
              id: this.getCurrentPage().id,
              skipped: true,
            },
            ...prevState.history.slice(1),
          ]
        }), () => {
          this.dispatch({
            type: 'navigate',
            value: 1,
          });
        });
      break;

      /* Option Selection */
      case 'setSelection':
        const { prompt, selections } = value;
        this.setValidState(
          (function(prevState) {
            return {
              ...prevState,
              version: this.program.version,
              selections: {
                ...prevState.selections,
                [prompt]: selections,
              },
            };
          }).bind(this), after
        );
      break;

      case 'reset':
        this.setState({
          ...this.getDefaultState(),
        }, after);
      break;

      default:
        console.warn('Unidentified action: ', action);
      break;
    }
  }

  setValidState(mutator, callback) {
    this.setState(
      (function(prevState) {
        let state = mutator(prevState);
        Object.keys(state.selections).forEach(
          (function(promptId) {
            const intPromptId = parseInt(promptId);
            const {
              limit: { max },
              options,
            } = this.getPromptById(intPromptId);

            // Grab selections
            let theseSelections = state.selections[intPromptId];

            if (theseSelections && theseSelections.length) {
              // Process deselections (which appear in state as redundant selections)
              const [ firstSelection ] = theseSelections;
              if (theseSelections.slice(1).includes(firstSelection)) {
                theseSelections = theseSelections.filter((selection) => selection !== firstSelection);
              }

              // Force-deselect any hidden options
              theseSelections = theseSelections.filter(
                (selection) => !options
                  .filter(
                    ({ matcher }) => !(matcher ? matcher : () => true)(state)
                  )
                  .map(
                    ({ value }) => value
                  )
                  .includes(selection)
              );

              // Enforce maximum selection limit
              theseSelections = theseSelections.slice(0, max);

              // Put selections
              state.selections[intPromptId] = theseSelections;
            }
          }).bind(this)
        );
        return state;
      }).bind(this), callback
    );
  }

  /* State Management */
  getDefaultState() {
    const { prompts } = this.program;

    // Clone default state
    let state = {
      ...defaultState,
    };

    // Pre-populate default values
    for (var x=0; x<prompts.length; x++) {
      state.selections[prompts[x].id] = prompts[x].defaults;
    }

    return state;
  }

  getInitialState() {
    if (useLocalStorage) {
      return JSON.parse(
        localStorage.getItem(localStorageName)
      ) || this.getDefaultState();
    } else {
      return this.getDefaultState();
    }
  }

  saveState() {
    if (useLocalStorage) {
      localStorage.setItem(
        localStorageName,
        JSON.stringify({
          version: program.version,
          ...this.state
        })
      );
    }
  }

  getPromptById(promptId) {
    const { prompts } = this.program;
    const x = prompts.find(({ id }) => id === promptId);
    return x;
  }

  /* Navigation Management */
  getPageByIndex(index) {
    const { id } = this.program.pages[index];
    return this.getPageById(id);
  }

  getPageById(pageId) {
    const { pages, prompts } = this.program;
    const pageProgram = pages.find(({ id }) => id === pageId);

    return {
      id: pageId,
      prompts: [
        ...prompts.filter(
          ({ id }) => pageProgram.prompts.includes(id)
        ),
      ],
      program: {
        ...pageProgram,
        index: pages.findIndex(({ id }) => id === pageId),
      },
      history: {
        ...this.state.history.find(({ id }) => id === pageId),
        index: this.state.history.findIndex(({ id }) => id === pageId),
      },
    }
  }

  getCurrentPage() {
    const { id } = this.state.history[0];
    return this.getPageById(id);
  }

  getNextPage(noSkip = false) {
    const {
      prompts,
      program: {
        index: currentPageIndex,
        skippable,
        nextPage = () => undefined
      }
    } = this.getCurrentPage();
    const nextInSequencePageId = (
      nextPage(this.state) || (
        program.pages.find(
          (page, index) => index > currentPageIndex
        ) ||
        { id: undefined }
      ).id
    );

    return (
      !prompts
        .map(
          (function({ id: promptId }) {;
            const {
              limit: { min }
            } = this.getPromptById(promptId);

            return (this.state.selections[promptId] || []).length >= min;
          }).bind(this)
        )
        .includes(false) || (skippable && !noSkip)
    ) && nextInSequencePageId !== undefined
      ? this.getPageById(nextInSequencePageId)
      : undefined;
  }

  getPreviousPage() {
    const { history: { index: currentIndex } } = this.getCurrentPage();
    return this.state.history.length-1 > currentIndex ? this.getPageByIndex(currentIndex + 1) : undefined;
  }

  render() {
    // Report current page to the host window
    const currentPage = this.getCurrentPage();
    this.onPageChange(
      currentPage.program.index
    );

    return (
      <div className="App">
        <Oracle
          utilities={{
            state: this.state,
            program,
            dispatch: this.dispatch.bind(this),
            getCurrentPage: this.getCurrentPage.bind(this),
            getPreviousPage: this.getPreviousPage.bind(this),
            getNextPage: this.getNextPage.bind(this),
            getPromptById: this.getPromptById.bind(this),
          }}
        />
      </div>
    );
  }
}

export default App;
